import type { CSSProperties, ReactNode } from "react";
import React, { Component } from "react";

import type { Dayjs } from "dayjs";
import { filter, find, get, isEqual } from "lodash";
import { connect } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { customRangeFormat, customRangeWithDayFormat } from "@similarweb/pro-logic-components";
import type { IMaxSelectableDurationRange, IPopupConfig } from "@similarweb/ui-components";
import { DurationSelectorStateless } from "@similarweb/ui-components";

// fallow-ignore-next-line boundary-violation -- shared duration selector uses trial utils; move to app-shared pending
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { swSettings } from "app/common/services/swSettings";
import { isPeriodOverPeriodToggleSupported } from "app/components/duration-selector/period-over-period/isPeriodOverPeriodToggleSupported";
import { IndicatorTypes } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/types";
import { DurationSelectorButton } from "pro-features/components/duration-selectors/DurationSelectorButton";
import type { IFilterBaseProps } from "app/components/filters-bar/FiltersBase";
import type { PropsOf } from "app/helpers/types/react";
import ConnectedPopup from "./ConnectedPopup";
import { ECompareType } from "./DurationSelectorTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";
import { PeriodOverPeriodService, periodOverPeriodService } from "app/services/PeriodOverPeriodService";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";
import { allTrackers } from "app/services/track/track";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import UnlockModalHookWrapper, {
    UnlockModalHookWrapperBind,
} from "pro-features/components/Modals/src/UnlockModal/UnlockModalHookWrapper";
import type { ISwSettings } from "app/@types/ISwSettings";
import { CLOSE_DURATION_FILTER, OPEN_DURATION_FILTER } from "./durationFilterActionTypes";
// fallow-ignore-next-line boundary-violation -- shared duration selector uses trial utils; move to app-shared pending
import TrialHook from "app/components/React/TrialHook/TrialHook";
import UIComponentStateService from "app/services/UIComponentStateService";

import {
    convertInitialPresetToDuration,
    getCompareDurationSelected,
    getSelectedDurationPresetId,
    isCompareDuration,
} from "./DurationSelectorUtils";

export type Presets = PropsOf<typeof DurationSelectorStateless>["presets"];
type IDurationTypeForTracking = "CUSTOM_RANGE" | "YEAR_OVER_YEAR" | "PRE_DEFINED_DURATION" | "PERIOD_OVER_PERIOD";

enum EGranularities {
    Weekly,
    Monthly,
}
interface IDurationSelectorSimpleProps extends IFilterBaseProps {
    minDate?: Dayjs;
    maxDate?: Dayjs;
    minDailyDate?: Dayjs;
    maxDailyDate?: Dayjs;
    isDisabled: boolean;
    presets?: Presets;
    weeklyPresets?: Presets;
    initialPreset: string;
    initialComparedDuration?: string | boolean;
    onSubmit: (duration: any, comparedDuration: any, durationTypeForTracking?: IDurationTypeForTracking) => void;
    onLockedItemClick?: () => void;
    componentName: string;
    keys: any;
    compareAllowed: boolean;
    compareLabel?: string;
    compareDisabledTooltipText?: string;
    hasPermissionsLock?: boolean;
    customDashboardState?: any;
    placement?: string;
    disableCalendar?: boolean;
    disableFooter?: boolean;
    isWeeklyDataAvailable?: boolean;
    showWeeklyToggle?: boolean;
    weeklyCalendarLocked?: ReactNode;
    close?: (action) => void;
    isShowPoPDropdownLockModal?: boolean;
    offsetDurationSelector?: boolean;
    periodOverPeriodCompareType?: IPeriodOverPeriodCompareType;
    isDaily?: boolean;
    sameDateClickedByUser?: boolean;
    presentCustomIndicator?: boolean;
    allowSingleDayPresentation?: boolean;
    isSingleDaySelection?: boolean;
    isSingleMonthSelection?: boolean;
    compareEnabledManually?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    onGranularityToggleClick?: (item: EGranularities) => void;
    withoutPresets?: boolean;
    maxDurationRange?: IMaxSelectableDurationRange;
}

export function formatDuration(duration, granularity = EGranularities.Monthly) {
    let format = customRangeFormat;
    if (granularity === EGranularities.Weekly) {
        format = customRangeWithDayFormat;
    }
    return `${duration.from.format(format)}-${duration.to.format(format)}`;
}

export class DurationSelectorSimple<P> extends Component<
    P & IDurationSelectorSimpleProps,
    {
        compareSelected: boolean;
        selectedPreset;
        compareDurationSelected;
        compareTypesSelected;
        compareTypeList;
        compareEnabled: boolean;
        isOpen: boolean;
        isUnlockOpen?: boolean;
        isBubbleOpen: boolean;
        selectedGranularity?: EGranularities;
        sameDateClickedByUser?: boolean;
    }
> {
    private _popService;

    private _swNavigator;

    private popupContainerRef;

    private _localStorageId;

    private _swSettings: ISwSettings;

    private _userLanguage;

    private lastWeeklyPreset: string;

    private lastMonthlyPreset: string;

    private buttonElm;

    public static defaultProps = {
        onToggle: (isOpen: boolean) => null,
        onLockedItemClick: () => ({}),
        height: 60,
        placement: "bottom-right",
        weeklyPresets: [],
        isWeeklyDataAvailable: false,
        weeklyCalendarLocked: null,
        initialPreset: "",
        presets: [],
        presentCustomIndicator: true,
        allowSingleDayPresentation: false,
    };

    constructor(props) {
        super(props);

        this._popService = periodOverPeriodService;
        this._swNavigator = proNavigator;
        this._localStorageId = "duration_selector_bubble";
        this._swSettings = swSettings;
        this._userLanguage = this._swSettings.components.Home.resources.Language || "en-us";
        this.buttonElm = React.createRef();
    }

    private getDurationTypeForTracking = (): IDurationTypeForTracking => {
        const {
            selectedPreset,
            compareSelected: isPeriodOverPeriod,
            compareTypesSelected: periodOverPeriodType,
        } = this.state;
        const isCustomRange = !selectedPreset;
        if (isPeriodOverPeriod) {
            return periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.YEAR
                ? "YEAR_OVER_YEAR"
                : "PERIOD_OVER_PERIOD";
        }
        if (isCustomRange) {
            return "CUSTOM_RANGE";
        }
        return "PRE_DEFINED_DURATION";
    };

    private onSubmit = () => {
        const { selectedPreset, compareDurationSelected, compareSelected, compareTypesSelected } = this.state;
        const durationTypeForTracking = this.getDurationTypeForTracking();
        allTrackers.trackEvent("Drop Down", "click", `Date Range/${selectedPreset ? selectedPreset : "Custom"}`);
        this.props.onSubmit(
            selectedPreset
                ? selectedPreset
                : formatDuration(compareDurationSelected.primary, this.state.selectedGranularity),
            compareSelected
                ? compareTypesSelected === "previous year"
                    ? "12m"
                    : DurationService.getDiffSymbol(
                          compareDurationSelected.primary.from,
                          compareDurationSelected.primary.to,
                          "months",
                      )
                : false,
            durationTypeForTracking,
        );
    };

    private onCancel = () => {
        this.setState(this._getState(this.props));
    };

    public componentDidMount() {
        this.setState(this._getState(this.props));
    }

    public componentDidUpdate(prevProps) {
        if (!isEqual(this.props, prevProps)) {
            this.setState(this._getState(this.props));
        }
    }

    protected getSelectedPreset = (props) => {
        const isCustom = props.initialPreset.indexOf("-") !== -1;
        const presetsToIterate = props.isDaily ? props.weeklyPresets : props.presets;
        const noAvailablePresets = presetsToIterate.every((p) => p.locked);
        const selectedPreset = isCustom || noAvailablePresets ? null : props.initialPreset;
        return selectedPreset;
    };

    private _getState = (props: IDurationSelectorSimpleProps) => {
        const { periodOverPeriodCompareType } = props;
        const isCustom = props.initialPreset.indexOf("-") !== -1;
        const compareSelected = !!props.initialComparedDuration || Boolean(periodOverPeriodCompareType);
        const selectedPreset = this.getSelectedPreset(props);
        let compareTypesSelected = "";
        let compareTypeList = null;
        let compareEnabled = false;
        const sameDateClickedByUser = props.sameDateClickedByUser;
        const duration = convertInitialPresetToDuration(props.initialPreset, [
            ...props.presets,
            ...props.weeklyPresets,
        ]);
        if (props.compareAllowed) {
            compareTypeList = this.getCompareTypeList(
                props.initialPreset,
                props.keys,
                props.componentName,
                props.initialPreset,
            );
            // compare to previous year by default
            const isComparedYear =
                (props.initialComparedDuration === false || props.initialComparedDuration === "12m") &&
                !compareTypeList.find((item) => item.id === PeriodOverPeriodService.COMPARE_TYPES.YEAR).disabled;
            compareTypesSelected = isComparedYear
                ? PeriodOverPeriodService.COMPARE_TYPES.YEAR
                : PeriodOverPeriodService.COMPARE_TYPES.PERIOD;
            if (periodOverPeriodCompareType) {
                compareTypesSelected = periodOverPeriodCompareType;
            }
            compareEnabled = this.isCompareDurationEnabled(
                duration,
                props.initialPreset === "28d",
                props.initialComparedDuration,
                props.keys,
                props.componentName,
                isCustom,
            );
        }

        const compareDurationSelected = getCompareDurationSelected(
            //from preset, from custom, + compare => convert to duration
            convertInitialPresetToDuration(props.initialPreset, [...props.presets, ...props.weeklyPresets]),
            compareSelected && compareEnabled,
            compareTypesSelected,
        );
        const isBubbleOpen = UIComponentStateService.getItem(this._localStorageId, "localStorage", true) !== "false";
        return {
            compareSelected,
            selectedPreset,
            compareDurationSelected,
            compareTypesSelected,
            compareTypeList,
            compareEnabled,
            sameDateClickedByUser,
            isOpen: false,
            isBubbleOpen,
            selectedGranularity: duration.isDaily ? EGranularities.Weekly : EGranularities.Monthly,
        };
    };

    public trackTypeToggle = (type) => {
        allTrackers.dispatchTrackEvent("Drop Down", "switch", `Change Duration Type/${type || ""}`);
    };

    public onGranularityChange = (index) => {
        this.props?.onGranularityToggleClick(index);

        let state: any = {};
        // weekly
        if (index === EGranularities.Weekly) {
            this.trackTypeToggle("week");
            state = {
                selectedGranularity: EGranularities.Weekly,
                compareSelected: false,
                compareEnabled: false,
            };
        } else {
            this.trackTypeToggle("month");
            state = {
                selectedGranularity: EGranularities.Monthly,
            };
        }
        this.setState(state, () => {
            if (index === EGranularities.Weekly) {
                // try to find the initial preset in the weekly presets
                // if not, take the first one as default
                const weeklyPreset = this.props.weeklyPresets.find((p) => p.id === this.lastWeeklyPreset);
                this.onPresetSelect()(weeklyPreset || this.props.weeklyPresets[0]);
            } else if (this.props.initialPreset.indexOf("-") === -1) {
                // try to find the initial preset in the presets
                // if not, take the 3m as default
                const monthlyPreset = this.props.presets.find((p) => p.id === this.lastMonthlyPreset);
                this.onPresetSelect()(monthlyPreset || this.props.presets.find((p) => p.id === "3m"));
            }
        });
    };

    public getPresets = () => {
        if (this.state.selectedGranularity === EGranularities.Weekly) {
            return this.props.weeklyPresets;
        } else {
            return this.props.presets;
        }
    };

    public onSameDateClickedByUser = (sameDateClickedByUser: boolean): void => {
        this.setState((state) => {
            return {
                ...state,
                sameDateClickedByUser: sameDateClickedByUser,
            };
        });
    };

    public getDurationSelectorStateless = () => {
        return (
            <DurationSelectorStateless
                locale={this._userLanguage}
                presets={this.getPresets()}
                selectedPreset={this.state.selectedPreset}
                presetsLabel="Date Range"
                cancelButtonText="Cancel"
                submitButtonText="Apply"
                customPresetText="Custom"
                compareAllowed={this.props.compareAllowed ? !isPeriodOverPeriodToggleSupported() : null}
                compareLabel={this.props.compareLabel}
                compareDisabledTooltipText={this.props.compareDisabledTooltipText}
                compareSelected={this.state.compareSelected}
                compareEnabled={
                    this.props.compareEnabledManually ? this.props.compareEnabledManually : this.state.compareEnabled
                }
                compareTypesList={this.state.compareTypeList}
                compareTypesSelected={this.state.compareTypesSelected}
                compareDurationSelected={this.state.compareDurationSelected}
                minDate={this.props.minDate}
                maxDate={this.props.maxDate}
                minDailyDate={this.props.minDailyDate}
                maxDailyDate={this.props.maxDailyDate}
                compareToggle={this.onCompareToggle}
                compareSelect={this.onCompareSelect}
                durationChange={this.onDurationChange}
                presetSelect={this.onPresetSelect(true)}
                selectorSubmit={this.onSubmit}
                hasPermissionsLock={this.props.hasPermissionsLock}
                disableCalendar={this.props.disableCalendar}
                disableFooter={this.props.disableFooter}
                renderContactUs={() => (
                    <UnlockModalHookWrapper
                        location={`${LocationService.getCurrentLocationTracking()}/Time Ranges`}
                        componentId="Prev button"
                        featureKey="WebAllowedDuration"
                    >
                        <SWReactIcons iconName="upgrade" />
                    </UnlockModalHookWrapper>
                )}
                itemWrapper={
                    this.props.isShowPoPDropdownLockModal
                        ? UnlockModalHookWrapperBind({
                              location: `${LocationService.getCurrentLocationTracking()}/Time Ranges`,
                              featureKey: "WebAllowedDuration",
                          })
                        : null
                }
                onDropdownButtonClick={this.onDropdownButtonClick}
                footerComponent={() => {
                    return (
                        this.showTrialHook() && (
                            <TrialHook
                                text="trial_hook.duration.text"
                                buttonText="trial_hook.duration.button.text"
                                iconName="daily-ranking"
                                label="Duration Selector"
                                onCtaClick={() => {
                                    openHooksModalV5("WebAllowedDuration");
                                }}
                            />
                        )
                    );
                }}
                onGranularityToggleClick={this.onGranularityChange}
                selectedGranularity={this.state.selectedGranularity as any}
                showWeeklyToggle={this.props.showWeeklyToggle}
                isWeeklyDataAvailable={this.props.isWeeklyDataAvailable}
                weeklyCalendarLocked={this.props.weeklyCalendarLocked}
                useDailyCalendar={this.props.isDaily}
                isSingleDaySelection={this.props.isSingleDaySelection}
                isSingleMonthSelection={this.props.isSingleMonthSelection}
                onSameDateClickedByUser={this.onSameDateClickedByUser}
                sameDateClickedByUser={this.state.sameDateClickedByUser}
                maxDurationRange={this.props.maxDurationRange}
            />
        );
    };

    private onCompareToggle = () => {
        allTrackers.trackEvent(
            "Checkbox",
            this.state.compareSelected ? "Remove" : "Add",
            `Date range/compare/${this.state.compareTypesSelected}`,
        );
        this.setState({
            compareSelected: !this.state.compareSelected,
            compareDurationSelected: getCompareDurationSelected(
                this.state.compareDurationSelected.primary,
                !isCompareDuration(this.state.compareSelected, this.state.compareEnabled, this.props.compareAllowed),
                this.state.compareTypesSelected,
            ),
        });
    };

    private clearCompare = (cb) => {
        this.setState(
            {
                compareSelected: false,
                compareDurationSelected: getCompareDurationSelected(
                    this.state.compareDurationSelected.primary,
                    false,
                    this.state.compareTypesSelected,
                ),
            },
            cb,
        );
    };

    private getCompareTypeList = (initialPreset, keys, componentName, symbol) => {
        const _items = this._popService
            .getPeriodOverPeriodDropdownItems(initialPreset, keys, componentName, false, symbol)
            .map((item, index) => {
                return {
                    id: item.id,
                    text: item.text,
                    value: index,
                    disabled: item.disabled,
                };
            });
        return _items;
    };

    private isCompareDurationEnabled = (
        duration,
        isWindow,
        initialComparedDuration,
        keys,
        componentName,
        isCustom = false,
    ) => {
        // currently no compare allowed on daily data
        if (duration.isDaily) {
            return false;
        }
        let _durationSymbol = DurationService.getDiffSymbol(duration.from, duration.to, isWindow ? "days" : "months");
        const _durationSymbol2 = DurationService.getDiffSymbol(
            duration.from,
            duration.to,
            isWindow ? "days" : "months",
        );
        if (isCustom) {
            _durationSymbol = formatDuration(duration, this.state?.selectedGranularity || EGranularities.Monthly);
        }

        const _periodOverPeriodEnabled = this._popService.periodOverPeriodEnabled(
            _durationSymbol,
            this.state?.compareDurationSelected.primary,
            keys,
            componentName,
            _durationSymbol2,
        );
        return _periodOverPeriodEnabled;
    };

    private onDurationChange = (duration) => {
        const isCustom = true; // seems like it is called just for custom dates
        const compareDurationSelected = getCompareDurationSelected(
            duration,
            isCompareDuration(this.state.compareSelected, this.state.compareEnabled, this.props.compareAllowed),
            this.state.compareTypesSelected,
        );
        const state: any = {
            compareTypesSelected: this.state.compareTypesSelected,
            compareDurationSelected,
            compareEnabled: this.isCompareDurationEnabled(
                duration,
                false,
                this.props.initialComparedDuration,
                this.props.keys,
                this.props.componentName,
                isCustom,
            ),
        };
        if (state.compareEnabled) {
            const durationSymbol = DurationService.getDiffSymbol(duration.from, duration.to, "days");
            let durationFormatted;
            if (isCustom) {
                durationFormatted = formatDuration(duration, this.state.selectedGranularity);
            }
            state.compareTypeList = this.getCompareTypeList(
                durationFormatted,
                this.props.keys,
                this.props.componentName,
                durationSymbol,
            );

            const disabledItems = filter(state.compareTypeList, {
                disabled: true,
            });
            // in case if we have selected period which valid for period but year selected
            // we'll select automatically wrong period
            if (disabledItems.length > 0) {
                if (disabledItems.length === 2) {
                    state.compareDurationSelected && (state.compareDurationSelected.secondary = null);
                    state.compareSelected = false;
                } else {
                    state.compareTypesSelected = get(find(state.compareTypeList, { disabled: false }), "id");
                    state.compareDurationSelected = getCompareDurationSelected(
                        duration,
                        isCompareDuration(
                            this.state.compareSelected,
                            this.state.compareEnabled,
                            this.props.compareAllowed,
                        ),
                        state.compareTypesSelected,
                    );
                }
            }
        } else {
            state.compareDurationSelected && (state.compareDurationSelected.secondary = null);
            state.compareSelected = false;
        }

        this.setState({
            ...state,
            selectedPreset: getSelectedDurationPresetId(
                compareDurationSelected,
                this.state.selectedGranularity === EGranularities.Weekly
                    ? this.props.weeklyPresets
                    : this.props.presets,
                "day",
            ),
        });
    };

    private setStateAfterPresetChange = (preset, compareEnabled) => {
        const { compareAllowed, keys, disableFooter, disableCalendar, componentName } = this.props;
        const newCompareTypeList = this.getCompareTypeList(preset.id, keys, componentName, preset.id);
        const isPreviousYearDisabled = newCompareTypeList.some(
            (compareType) => compareType.id === ECompareType.PreviousYear && compareType.disabled === true,
        );
        const { compareTypesSelected } = this.state;
        const shouldOverrideCompareTypesSelected =
            isPreviousYearDisabled && compareTypesSelected === ECompareType.PreviousYear;
        this.setState(
            {
                compareDurationSelected: getCompareDurationSelected(
                    preset.value,
                    isCompareDuration(this.state.compareSelected, this.state.compareEnabled, compareAllowed),
                    this.state.compareTypesSelected,
                ),
                compareTypeList: newCompareTypeList,
                selectedPreset: preset.id,
                compareEnabled,
                compareTypesSelected: shouldOverrideCompareTypesSelected
                    ? ECompareType.PreviousPeriod
                    : compareTypesSelected,
            },
            () => {
                if (disableFooter && disableCalendar) {
                    this.onSubmit();
                }
            },
        );
    };

    protected onPresetSelect =
        (shouldTrack = false) =>
        (preset) => {
            if (this.props.withoutPresets) {
                return;
            }

            if (shouldTrack) {
                allTrackers.dispatchTrackEvent("Drop Down", "click", `Select Duration Preset/${preset.id || ""}`);
            }
            // save the preset to we can use it when granularity changed
            switch (this.state.selectedGranularity) {
                case EGranularities.Weekly:
                    this.lastWeeklyPreset = preset.id;
                    break;
                case EGranularities.Monthly:
                    this.lastMonthlyPreset = preset.id;
            }
            if (preset.locked) {
                openHooksModalV5("WebAllowedDuration");
                if (this.popupContainerRef) {
                    this.props.close({ type: CLOSE_DURATION_FILTER });
                }

                allTrackers.trackEvent(
                    "hook/Contact Us/Pop up",
                    "click",
                    `Time Filter/${preset ? preset.id : "Custom"}`,
                );
                return;
            }

            const compareEnabled = this.isCompareDurationEnabled(
                preset.value,
                preset.id === "28d",
                this.props.initialComparedDuration,
                this.props.keys,
                this.props.componentName,
            );
            if (this.state.compareEnabled !== compareEnabled) {
                this.clearCompare(() => {
                    //callback required because setStateAfterPresetChange() depends on changed state after clearCompare()
                    this.setStateAfterPresetChange(preset, compareEnabled);
                });
            } else {
                this.setStateAfterPresetChange(preset, compareEnabled);
            }
        };

    private onCompareSelect = (compareType) => {
        allTrackers.trackEvent("Drop Down", "Click", `Date range/compare/${compareType.id}`);
        this.setState({
            compareTypesSelected: compareType.id,
            compareDurationSelected: getCompareDurationSelected(
                this.state.compareDurationSelected.primary,
                isCompareDuration(this.state.compareSelected, this.state.compareEnabled, this.props.compareAllowed),
                compareType.id,
            ),
        });
    };

    private onDropdownButtonClick = () => {
        allTrackers.trackEvent("Drop Down", "Open", "Date range/compare");
    };

    private showTrialHook = () => {
        // TODO: liorb - use unified flag
        const Home = this._swSettings.components.Home.resources;
        const isNoTouch = Home.IsNoTouchUser;
        return isTrialOrAfterTrial() || isNoTouch;
    };

    public render() {
        const {
            initialPreset,
            presets,
            weeklyPresets,
            placement,
            presentNotificationIndicator,
            presentNotificationTooltipKey,
            offsetDurationSelector,
        } = this.props;
        const trialContainerModifier = !isTrialOrAfterTrial() ? "" : " FiltersBarDropdown--trial";
        const classesList = offsetDurationSelector
            ? `DropdownButton-popup-container DurationSelector-popup-container FiltersBarDropdown${trialContainerModifier} DurationSelector-offsetLeft`
            : `DropdownButton-popup-container DurationSelector-popup-container FiltersBarDropdown${trialContainerModifier}`;
        const popupConfig: IPopupConfig = {
            placement,
            autoPlacement: false,
            cssClassContainer: classesList,
            enabled: !this.props.isDisabled,
            onToggle: (isOpen, isOutsideClick) => {
                if (!isOpen && isOutsideClick) {
                    // reset state when closing the popup by outside click
                    this.setState(this._getState(this.props));
                }
                this.setState({ isOpen }, () => {
                    this.props.onToggle(isOpen, isOutsideClick);
                });
            },
        };
        const style: CSSProperties = {
            textOverflow: "ellipsis",
            overflow: "hidden",
        };
        const isCompareDurationFlag = this.props.initialComparedDuration; //isCompareDuration(this.state.compareSelected, this.state.compareEnabled, this.props.compareAllowed) &&
        return (
            <ConnectedPopup
                stateKey="WebsiteAnalysisFilters.durationFilter"
                appendTo={this.props.appendTo}
                config={popupConfig}
                content={this.getDurationSelectorStateless}
                ref={(popup) => {
                    this.popupContainerRef = popup;
                }}
                openAction={{
                    type: OPEN_DURATION_FILTER,
                }}
                closeAction={{
                    type: CLOSE_DURATION_FILTER,
                }}
                onOpen={this.props.onOpen}
                onClose={this.props.onClose}
            >
                <div
                    ref={(el) => {
                        this.buttonElm = el;
                    }}
                >
                    {this.props.children ? (
                        this.props.children
                    ) : (
                        <DurationSelectorButton
                            height={this.props.height}
                            isDisabled={this.props.isDisabled}
                            presets={[...presets, ...weeklyPresets]}
                            initialPreset={initialPreset}
                            isCompare={!!isCompareDurationFlag}
                            presentCustomIndicator={this.props.presentCustomIndicator}
                            allowSingleDayPresentation={this.props.allowSingleDayPresentation}
                            presentNotificationIndicator={presentNotificationIndicator}
                            presentNotificationTooltipKey={presentNotificationTooltipKey}
                            indicatorType={IndicatorTypes.DOT}
                        />
                    )}
                </div>
            </ConnectedPopup>
        );
    }
}
const mapDispatchToProps = (dispatch) => {
    return {
        close: (action) => {
            dispatch(action);
        },
    };
};
export default connect(null, mapDispatchToProps, null, { forwardRef: true })(DurationSelectorSimple);
